import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarDays, FileStack, Globe, Lightbulb, Newspaper, Quote, Settings2, SlidersHorizontal } from "lucide-react";
import { defineModuleUi, from, unwrap, useOrg } from "@vixio/data";
import { formatDate, todayIso, addDaysIso, lazyPage } from "@vixio/ui";
import { CHANNEL_LABEL, type CalendarItem, type Channel } from "./api";
import { StructurePage } from "./StructurePage";
import { SiteSettingsPage } from "./SiteSettingsPage";

export * from "./api";
export { CheckReportView, EntryPreview } from "./shared";
export { menuToText, textToMenu } from "./StructurePage";
export { SiteStatusLine } from "./SiteSettingsPage";
export { StructuredFields, blankLike, fieldLabel } from "./StructuredFields";

// Telas carregadas sob demanda: cada uma vira um arquivo à parte no build.
const EntriesPage = lazyPage(() => import("./EntriesPage"), "EntriesPage");
const EntryEditorPage = lazyPage(() => import("./EntryEditorPage"), "EntryEditorPage");
const IdeasPage = lazyPage(() => import("./IdeasPage"), "IdeasPage");
const IdeaPage = lazyPage(() => import("./IdeaPage"), "IdeaPage");
const EvidencesPage = lazyPage(() => import("./EvidencesPage"), "EvidencesPage");
const CalendarPage = lazyPage(() => import("./CalendarPage"), "CalendarPage");
const PlanningPage = lazyPage(() => import("./PlanningPage"), "PlanningPage");
const PreviewPublicPage = lazyPage(() => import("./PreviewPublicPage"), "PreviewPublicPage");

/** Início: o que está planejado ou agendado nos próximos 7 dias. */
function NextWeekWidget() {
  const org = useOrg();
  const from0 = todayIso();
  const until = addDaysIso(from0, 7);
  const q = useQuery({
    queryKey: ["content", "calendar", "widget", org.id, from0],
    queryFn: () => unwrap<CalendarItem[]>(from("content", "v_calendar").select("*").eq("org_id", org.id).gte("day", from0).lte("day", until).neq("kind", "publicado").order("day").limit(8)),
  });
  if (!q.data?.length) return <p className="muted">Nada planejado para os próximos 7 dias.</p>;
  return (
    <ul className="vx-stack" style={{ gap: 6, margin: 0, padding: 0, listStyle: "none" }}>
      {q.data.map((it, i) => (
        <li key={i} className="vx-row" style={{ flexWrap: "nowrap", alignItems: "baseline" }}>
          <span className="num text-2" style={{ width: 44, flexShrink: 0 }}>{formatDate(it.day).slice(0, 5)}</span>
          <span style={{ minWidth: 0 }}>
            <Link to={it.kind === "pauta" ? `/conteudo/pautas/${it.ref_id}` : `/conteudo/${it.format === "page" ? "site" : "blog"}/${it.ref_id}`}>{it.title}</Link>
            <span className="text-2"> · {CHANNEL_LABEL[it.channel as Channel] ?? it.channel}{it.kind === "agendado" ? " (agendado)" : ""}</span>
          </span>
        </li>
      ))}
    </ul>
  );
}

export default defineModuleUi({
  key: "content",
  title: "Conteúdo",
  domain: "content",
  icon: Globe,
  menu: [
    { path: "/conteudo/site", label: "Site", minRole: "member", icon: Globe, order: 10, keywords: "páginas site blocos versão publicar prévia" },
    { path: "/conteudo/blog", label: "Blog", minRole: "member", icon: Newspaper, order: 15, keywords: "post artigo seo publicar agendar" },
    { path: "/conteudo/calendario", label: "Calendário editorial", minRole: "member", icon: CalendarDays, order: 20, keywords: "calendário semana-padrão agenda editorial" },
    { path: "/conteudo/pautas", label: "Pautas", minRole: "member", icon: Lightbulb, order: 25, keywords: "pauta roteiro 6 blocos verificador gancho" },
    { path: "/conteudo/evidencias", label: "Evidências", minRole: "member", icon: Quote, order: 30, keywords: "evidência fonte E01 E60 dados" },
    { path: "/conteudo/planejamento", label: "Planejamento", minRole: "member", icon: SlidersHorizontal, order: 35, keywords: "pilares peso semana verificador regras métricas" },
    { path: "/conteudo/estrutura", label: "Estrutura do site", minRole: "member", icon: FileStack, order: 40, keywords: "menu redirecionamento 301 faq depoimento mídia configuração" },
    { path: "/conteudo/configuracoes-do-site", label: "Configurações do site", minRole: "member", icon: Settings2, order: 12, keywords: "contato whatsapp e-mail horário endereço redes logo seo atualizar site deploy gancho coolify" },
  ],
  routes: [
    { path: "/conteudo/site", element: <EntriesPage kind="page" />, minRole: "member" },
    { path: "/conteudo/site/:id", element: <EntryEditorPage kind="page" />, minRole: "member" },
    { path: "/conteudo/blog", element: <EntriesPage kind="post" />, minRole: "member" },
    { path: "/conteudo/blog/:id", element: <EntryEditorPage kind="post" />, minRole: "member" },
    { path: "/conteudo/calendario", element: <CalendarPage />, minRole: "member" },
    { path: "/conteudo/pautas", element: <IdeasPage />, minRole: "member" },
    { path: "/conteudo/pautas/:id", element: <IdeaPage />, minRole: "member" },
    { path: "/conteudo/evidencias", element: <EvidencesPage />, minRole: "member" },
    { path: "/conteudo/planejamento", element: <PlanningPage />, minRole: "member" },
    { path: "/conteudo/estrutura", element: <StructurePage />, minRole: "member" },
    { path: "/conteudo/configuracoes-do-site", element: <SiteSettingsPage />, minRole: "member" },
  ],
  widgets: [{ key: "content-next-week", title: "Conteúdo nos próximos 7 dias", minRole: "member", span: 1, order: 60, element: NextWeekWidget }],
  publicRoutes: [{ path: "/previa/:token", element: <PreviewPublicPage /> }],
});
